import { Grid, useMediaQuery } from "@mui/material";
import { dripOfferAssets } from "assets/cdn";
import { useWebStoreContext } from "contexts";
import { IShopRewardItem } from "contexts/WebStoreProvider/types";
import { useImagePath } from "hooks/useImagePath";
import useRewardsById from "hooks/useRewardsById";
import { DripOfferResponse, Node } from "interfaces/drip-offer";
import { FeatureType } from "interfaces/feature-tracker";
import { handleTicketVariant } from "pages/DripOffer/hooks";
import {
	ClaimedSmallTicket,
	Shine,
	SmallTicket,
	SmallTicketTiki
} from "pages/DripOffer/styles";
import { useEffect } from "react";
import { featureTrackerAPI } from "redux/apis";

interface NodeProps {
	node: Node;
	onSetAvailableNode: (node: Node) => void;
	changeSize: boolean;
	availableNode?: Node;
	isPopup?: boolean;
	offerPurchaseDate?: string;
}

export const DisplayNode = ({
	node,
	onSetAvailableNode,
	changeSize,
	availableNode,
	isPopup,
	offerPurchaseDate
}: NodeProps) => {
	const isSmall = useMediaQuery("(max-width: 754px)");
	const isPortrait = useMediaQuery("(orientation: portrait)");
	const { getImage } = useImagePath("cdn");
	const collectedNodeImage = getImage(dripOfferAssets.collectedNode);
	const shineImage = getImage(dripOfferAssets.shine);
	const tikiImage = getImage(dripOfferAssets.tiki);
	const unclaimedNodeImage = getImage(dripOfferAssets.unclaimedNode);
	const { authToken } = useWebStoreContext();
	const { data }: { data?: DripOfferResponse } =
		featureTrackerAPI.endpoints.getFeatureTrackers.useQueryState({
			FeatureType: FeatureType.DRIP_OFFER,
			isAuthenticated: !!authToken?.value
		});

	useEffect(() => {
		if (!data) {
			return;
		}
		!availableNode && node.state === "available" && onSetAvailableNode(node);
	}, [data]);

	const items = useRewardsById(node.rewardId);

	if (!isPopup) {
		return (
			<Grid
				item
				width={{ xs: "8.6em", sm: changeSize ? "14.285%" : "16.66%" }}
				position="relative"
			>
				{(node.state === "claimed" || node.state === "disqualified") && (
					<ClaimedSmallTicket
						src={
							node.state === "claimed" ? collectedNodeImage : unclaimedNodeImage
						}
						alt="Collected node state image"
					/>
				)}
				{node.state === "available" && (
					<>
						<Shine src={shineImage} alt="Shiny style node state image" />
						<SmallTicketTiki src={tikiImage} alt="Tiki saying hi" />
					</>
				)}
				<SmallTicket
					container
					direction="column"
					background={handleTicketVariant(
						node.rewardBackgroundImageUrl,
						node.state === "available"
					)}
					claimed={
						node.state === "claimed" || node.state === "disqualified"
							? "true"
							: "false"
					}
				>
					<Grid
						item
						position="relative"
						display="flex"
						justifyContent="center"
						alignItems="center"
					>
						<div className="item-content small">
							<img src={items?.[0]?.imageUrl} alt="" />
							<p
								className={`small ${items?.[0]?.id === "coin" && "is-coin"} ${
									Number(items?.[0]?.description?.length) > 6 &&
									offerPurchaseDate &&
									"is-large-number"
								}`}
							>
								{items?.[0]?.description}
							</p>
						</div>
					</Grid>
					<Grid item md={1} lg={1} px={0.5} position="relative">
						<p className="prize-name">Prize {node.nodeIndex + 2}</p>
					</Grid>
				</SmallTicket>
			</Grid>
		);
	} else {
		return (
			<Grid
				item
				xs={isPortrait && isSmall ? 4 : changeSize ? 1.714 : 2}
				position="relative"
			>
				{(node.state === "claimed" || node.state === "disqualified") && (
					<ClaimedSmallTicket
						src={
							node.state === "claimed" ? collectedNodeImage : unclaimedNodeImage
						}
						alt="Collected node state image"
					/>
				)}
				{node.state === "available" && (
					<>
						<Shine src={shineImage} alt="Shiny style node state image" />
						<SmallTicketTiki
							src={tikiImage}
							alt="Tiki saying hi"
							isPopup={isPopup}
						/>
					</>
				)}
				<SmallTicket
					container
					direction="column"
					background={handleTicketVariant(
						node.rewardBackgroundImageUrl,
						node.state === "available"
					)}
					claimed={
						node.state === "claimed" || node.state === "disqualified"
							? "true"
							: "false"
					}
					ispopup="true"
				>
					<Grid
						item
						position="relative"
						display="flex"
						justifyContent="center"
						alignItems="center"
					>
						<div className="item-content small">
							<img src={items?.[0]?.imageUrl} alt="" />
							<p className={`small ${items?.[0]?.id === "coin" && "is-coin"}`}>
								{items?.[0]?.description}
							</p>
						</div>
					</Grid>
					<Grid item px={0.5}>
						<p className="prize-name">Prize {node.nodeIndex + 2}</p>
					</Grid>
				</SmallTicket>
			</Grid>
		);
	}
};

interface DisplayFirstNodeProps {
	items: IShopRewardItem[];
	background: string;
	isPopup?: boolean;
}

export const DisplayFirstNode = ({
	items,
	background,
	isPopup
}: DisplayFirstNodeProps) => {
	const isSmall = useMediaQuery("(max-width: 754px)");
	const isPortrait = useMediaQuery("(orientation: portrait)");
	const { getImage } = useImagePath("cdn");
	const collectedNodeImage = getImage(dripOfferAssets.collectedNode);
	const itemValue = Number(items?.[0]?.value);

	if (!isPopup) {
		return (
			<Grid item width={{ xs: "8.6em", sm: "14.285%" }} position="relative">
				<ClaimedSmallTicket
					src={collectedNodeImage}
					alt="Collected node image"
				/>
				<SmallTicket
					container
					direction="column"
					background={background}
					claimed={"true"}
				>
					<Grid
						item
						position="relative"
						display="flex"
						justifyContent="center"
						alignItems="center"
					>
						<div className="item-content small">
							<img src={items[0]?.imageUrl} alt="" />
							<p
								className={`small ${items[0].id === "coin" && "is-coin"} ${
									itemValue > 99999 && "is-large-number"
								}`}
							>
								{itemValue.toLocaleString("en-US")}
							</p>
						</div>
					</Grid>
					<Grid item md={1} lg={1} px={0.5} position="relative">
						<p className="prize-name">Prize 1</p>
					</Grid>
				</SmallTicket>
			</Grid>
		);
	} else {
		return (
			<Grid item xs={isPortrait && isSmall ? 4 : 1.714} position="relative">
				<ClaimedSmallTicket
					src={collectedNodeImage}
					alt="Collected node image"
				/>
				<SmallTicket
					container
					direction="column"
					background={background}
					claimed={"true"}
					ispopup="true"
				>
					<Grid
						item
						xs={11}
						position="relative"
						display="flex"
						justifyContent="center"
						alignItems="center"
					>
						<div className="item-content small">
							<img src={items[0]?.imageUrl} alt="" />
							<p className={`small ${items[0].id === "coin" && "is-coin"}`}>
								{itemValue.toLocaleString("en-US")}
							</p>
						</div>
					</Grid>
					<Grid item xs={1} px={0.5}>
						<p className="prize-name">Prize 1</p>
					</Grid>
				</SmallTicket>
			</Grid>
		);
	}
};
